import {values} from 'ramda'

export const PATHS = {
  home: '/',
  login: '/login',
  loginCallback: '/login/callback',
  signup: '/sign-up',
  signupConfirm: '/sign-up/confirm',
  emailVerification: '/email-verification',
  requestPasswordReset: '/request-password-reset',
  passwordReset: '/password-reset',
  twoFactorSetup: '/two-factor-setup',
  notFound: '/404',
  sharepointCallback: '/sharepoint/callback',
  guruPublicShare: '/share/guru/:token',
  // Public QR landing pages (short paths because QR density matters —
  // every char in the encoded URL is more printable area). Auth-gated
  // at the component level: if the visitor is signed in they're routed
  // straight to the unit / request-access flow; otherwise they see a
  // sign-up CTA that round-trips back here once verified.
  unitQrLanding: '/u/:unitId',
  buildingQrLanding: '/b/:bpId/request-access',
  abandonedCart: '/abandoned-cart/:token',
}

export const PRIVATE_PATHS = {
  dashboard: '/dashboard',
  bpList: '/bp-list',
  createBp: '/create-bp',
  editBp: '/edit-bp/:id',
  bpDetails: '/bp-list/:id/:folder?/:unitId?',
  guruShare: '/bp-list/:id/guru-shares/:shareId',
  portfolioGuruShare: '/portfolios/:portfolioId/guru-shares/:shareId',
  guruSource: '/bp-list/:id/source/:fileId',
  draftDetails: '/drafts/:id/:folder?/:unitId?',
  savedPassports: '/favourites',
  savedDetails: '/favourites/:id/:folder?/:unitId?',
  profileSettings: '/profile',
  twoFactorSetup: '/two-factor-setup',
  notificationsCenter: '/notifications-center',
  documentAccessRequests: '/document-access-requests',
  organisation: '/organisation',
  cart: '/cart',
  terms: '/terms',
  drafts: '/drafts',
  paymentSuccess: '/cart/success',
  paymentError: '/cart/failed',
  billingList: '/billing-list',
  accessRequests: '/access-requests',
  analytics: '/analytics',
  addAnalytics: '/analytics/add',
  editAnalytics: '/analytics/edit',
  fileUpload: '/files/upload/:id',
  // Task Management
  taskManagement: '/tasks',
  taskDetails: '/tasks/:id',
  taskSeries: '/tasks/series/:id',
  taskObligation: '/tasks/obligation/:id',
  taskDomains: '/tasks/domains',
  createTask: '/tasks/create',
  calendar: '/calendar',
  calendarSubscribe: '/calendar/subscribe',
  formBuilder: '/forms/builder',
  formBuilderEdit: '/forms/builder/:id',
  notificationPreferences: '/notification-preferences',
  // Portfolios
  portfolios: '/portfolios',
  portfoliosArea: '/portfolios',
  portfoliosAreaEstate: '/portfolios/:portfolioId/estates/:estateId',
  portfolioDetail: '/portfolios/:id',
  estateDetail: '/estates/:id',
  // Actions
  guru: '/guru',
  actions: '/actions',
  actionDetail: '/actions/:id',
  actionsReview: '/actions/review',
  // Incidents
  incidents: '/incidents',
  incidentDetail: '/incidents/:id',
  // Contract Management
  contractManagement: '/contract-management',
  contractors: '/contractors',
  // Compliance
  compliance:      '/bp-list/:bpId/compliance',
  programme:       '/bp-list/:bpId/programme',
  legislation:     '/bp-list/:bpId/compliance/legislation',
  remedials:       '/bp-list/:bpId/remedials',
  remedialsReview: '/bp-list/:bpId/remedials/review',
  obligationSetup: '/bp-list/:bpId/compliance/setup',
  assetRegister:   '/bp-list/:bpId/compliance/assets',
  permits:         '/bp-list/:bpId/compliance/permits',
  permitPrint:     '/bp-list/:bpId/compliance/permits/:permitId/print',
  exitReadiness:   '/bp-list/:bpId/handover',
  portfolioReport: '/dashboard/report',
  kpiReport:       '/dashboard/kpi-report',
  buildingsAtRisk: '/dashboard/at-risk',
}

let navigateFunction

/**
 * Sets the navigate function from React Router.
 * This should be called once inside a component.
 * @param {Function} navigate - The navigate function from React Router.
 */
export const setNavigate = navigate => {
  navigateFunction = navigate
}

/**
 * Redirects to the specified path with optional state.
 * Can be used anywhere in your application.
 * @param {string} path - The path to navigate to.
 * @param {object} [state] - Optional state to pass.
 */
export const redirect = (path, state) => {
  if (navigateFunction) {
    navigateFunction(path, {state})
  } else {
    console.error(
      'Navigate function has not been set. Ensure that setNavigate is called inside a component.'
    )
  }
}

export const isExcludedFromFetchRedirect = path => {
  if (values(PATHS).includes(path)) return true
  // QR landing routes are parameterized (e.g. /u/abc-123) so an exact
  // includes() never matches. Treat anything under /u/ or /b/.../request-access
  // as public — auth fetch is still allowed to run for these (so signed-in
  // users get auto-redirected to the auth'd unit page), but a 401 must
  // NOT bounce an anonymous visitor away from the landing card.
  if (/^\/u\/[^/]+$/.test(path)) return true
  if (/^\/b\/[^/]+\/request-access$/.test(path)) return true
  if (/^\/abandoned-cart\/[^/]+$/.test(path)) return true
  return false
}
